import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Search, Move, Plus, GripVertical, Trash2, Edit2, Check, X,
  ChevronRight, ChevronDown, Sparkles, Loader2, RefreshCw, ArrowDownAZ, RotateCcw,
  ArrowUp, ArrowDown, ChevronsUp, ChevronsDown,
} from 'lucide-react';
import { COLORS, formatCurrency } from '../../lib/utils';
import { mappingsAPI, aiAPI } from '../../lib/api';
import MappingRepairPanel from './MappingRepairPanel';

/**
 * FathomMapLayer — Fathom-style Chart of Accounts mapping screen
 * Flat-tree data model. Auto-buckets accounts by QBO type. Drag-and-drop
 * to reorder/combine. Add custom headings to group accounts under.
 */

// Section definitions — fixed top-level buckets
const SECTIONS = {
  income_statement: [
    { id: 'sec-revenue',    label: 'Revenue',                  mappingKey: 'income',  color: COLORS.success, types: ['Income'] },
    { id: 'sec-cogs',       label: 'Cost of Goods Sold',       mappingKey: 'cogs',    color: COLORS.error,   types: ['COGS'] },
    { id: 'sec-opex',       label: 'Operating Expenses',       mappingKey: 'opex',    color: '#D97706',      types: ['Expense'] },
    { id: 'sec-other-inc',  label: 'Other Income',             mappingKey: 'other_income',  color: '#0EA5E9',      types: ['Other Income'] },
    { id: 'sec-other-exp',  label: 'Other Expense',            mappingKey: 'other_expense', color: '#64748B',      types: ['Other Expense'] },
  ],
  balance_sheet: [
    { id: 'sec-cash',          label: 'Cash & Equivalents',     mappingKey: 'cash',                color: '#06B6D4', types: ['Bank'] },
    { id: 'sec-curr-assets',   label: 'Current Assets',         mappingKey: 'assets_current',      color: '#3B82F6', types: ['Current Asset'] },
    { id: 'sec-fix-assets',    label: 'Fixed Assets',           mappingKey: 'assets_fixed',        color: '#6366F1', types: ['Fixed Asset'] },
    { id: 'sec-other-asset',   label: 'Other Assets',           mappingKey: 'assets_other',        color: '#8B5CF6', types: ['Other Asset'] },
    { id: 'sec-curr-liab',     label: 'Current Liabilities',    mappingKey: 'liabilities_current', color: '#EF4444', types: ['Current Liability'] },
    { id: 'sec-short-debt',    label: 'Short Term Debt',        mappingKey: 'debt_short',          color: '#F97316', types: ['Short-Term Debt'] },
    { id: 'sec-long-liab',     label: 'Long-Term Liabilities',  mappingKey: 'liabilities_long',    color: '#DC2626', types: ['Long-Term Liability'] },
    { id: 'sec-equity',        label: 'Equity',                 mappingKey: 'equity',              color: '#10B981', types: ['Equity'] },
  ],
};

// Map each section ID → which legacy QBO "type" categories are allowed in it.
// This enforces accounting integrity: an Equity account can NEVER appear in a P&L section,
// and a P&L account can NEVER appear in a Balance Sheet section, regardless of saved tree.
const SECTION_ALLOWED_TYPES = {
  // Income Statement
  'sec-revenue':     new Set(['Income']),
  'sec-cogs':        new Set(['COGS']),
  'sec-opex':        new Set(['Expense']),
  'sec-other-inc':   new Set(['Income', 'Other']),
  'sec-other-exp':   new Set(['Expense', 'Other']),
  // Balance Sheet
  'sec-cash':         new Set(['Asset']),
  'sec-curr-assets':  new Set(['Asset']),
  'sec-fix-assets':   new Set(['Asset']),
  'sec-other-asset':  new Set(['Asset']),
  'sec-curr-liab':    new Set(['Liability']),
  'sec-short-debt':   new Set(['Liability']),
  'sec-long-liab':    new Set(['Liability']),
  'sec-equity':       new Set(['Equity']),
};

// Display sign for each section's accounts. Backend now returns natural debit-credit
// signed balances. Credit-natural sections (Revenue, Other Income, Liabilities, Equity)
// need to be flipped for display so users see positive numbers in the report.
const SECTION_DISPLAY_SIGN = {
  'sec-revenue':      -1,
  'sec-cogs':         +1,
  'sec-opex':         +1,
  'sec-other-inc':    -1,
  'sec-other-exp':    +1,
  'sec-cash':         +1,
  'sec-curr-assets':  +1,
  'sec-fix-assets':   +1,
  'sec-other-asset':  +1,
  'sec-curr-liab':    -1,
  'sec-short-debt':   -1,
  'sec-long-liab':    -1,
  'sec-equity':       -1,
};

// Classify a raw account into a section bucket
function classifyAccount(account) {
  // Prefer detailed QBO type when available (set by backend on QBO sync)
  const qboType = (account.qbo_type || '').toLowerCase();
  const type = account.type || 'Other';
  const name = (account.name || '').toLowerCase();

  // Income Statement
  if (type === 'Income') {
    if (qboType.includes('other income')) return 'sec-other-inc';
    return 'sec-revenue';
  }
  if (type === 'COGS') return 'sec-cogs';
  if (type === 'Expense') {
    if (qboType.includes('other expense')) return 'sec-other-exp';
    return 'sec-opex';
  }
  if (type === 'Other') {
    // Heuristic — split by sign or name
    if (name.includes('expense') || name.includes('cost')) return 'sec-other-exp';
    return 'sec-other-inc';
  }

  // Balance Sheet
  if (type === 'Asset') {
    // Bank → top-level Cash & Equivalents section
    if (qboType === 'bank' || qboType.includes('bank')) return 'sec-cash';
    if (qboType.includes('fixed asset')) return 'sec-fix-assets';
    if (qboType.includes('other asset')) return 'sec-other-asset';
    // Fallback heuristics for non-QBO data
    if (/equipment|building|land|vehicle|furniture|depr|machinery/.test(name)) return 'sec-fix-assets';
    if (/\bcash\b|checking|savings|money market|operating account|payroll account/.test(name)) return 'sec-cash';
    return 'sec-curr-assets';
  }
  if (type === 'Liability') {
    // Credit Card and Line of Credit → Short Term Debt
    if (qboType.includes('credit card')) return 'sec-short-debt';
    if (/line of credit|\bloc\b/.test(name)) return 'sec-short-debt';
    if (qboType.includes('long term') || qboType.includes('long-term')) return 'sec-long-liab';
    if (/note payable|mortgage|long.?term|loan/.test(name)) {
      return 'sec-long-liab';
    }
    return 'sec-curr-liab';
  }
  if (type === 'Equity') return 'sec-equity';

  return 'sec-other-exp';
}

// Pattern-based sub-heading rules: each account name gets matched to a sub-heading
// per its parent section. Order matters — first match wins.
const SUB_HEADING_RULES = {
  'sec-revenue': [
    { label: 'Sales',            match: /\bsales\b|product sales|service revenue|sales income|merchandise/i },
    { label: 'Service Income',   match: /\bservice\b|consult|maintenance|fee income/i },
    { label: 'Other Revenue',    match: /shipping|delivery|finance|interest income/i },
  ],
  'sec-cogs': [
    { label: 'Materials & Supplies', match: /material|suppl|inventory|merchand|goods/i },
    { label: 'Direct Labor',         match: /labor|wages|contract labor|subcontract/i },
    { label: 'Other Direct Costs',   match: /freight|shipping|fuel|cogs|cost of/i },
  ],
  'sec-opex': [
    { label: 'Salaries & Wages',    match: /salar|wages|bonus|commission/i },
    { label: 'Payroll Taxes',       match: /payroll tax|fica|medicare|sui|futa|suta/i },
    { label: 'Employee Benefits',   match: /benefit|insurance health|retirement|401|pension|life insurance|medical|dental|vision/i },
    { label: 'Rent & Occupancy',    match: /rent|lease (?!auto|vehicle)|occupancy|building expense/i },
    { label: 'Utilities',           match: /utilit|electric|gas (?!fuel)|water|sewer|internet|telephone|phone|cell|cable/i },
    { label: 'Insurance',           match: /insurance(?! health| medical| dental| vision| benefits)|liability/i },
    { label: 'Marketing & Advertising', match: /advertis|marketing|promotion|seo|social|website expense/i },
    { label: 'Professional Fees',   match: /professional|legal|accounting|consult|audit|tax prep/i },
    { label: 'Office Expenses',     match: /office|supplies|postage|printing|stationery|software|subscription|computer/i },
    { label: 'Repairs & Maintenance', match: /repair|maintenance|cleaning|janitor|landscap/i },
    { label: 'Travel & Entertainment', match: /travel|meal|entertain|lodging|hotel|airfare|mileage|per diem/i },
    { label: 'Vehicle Expenses',    match: /vehicle|auto|truck|gas (?:fuel)?|fuel|registration/i },
    { label: 'Depreciation & Amortization', match: /depreciat|amortiz/i },
    { label: 'Interest Expense',    match: /interest expense|finance charge|bank fee|service charge/i },
    { label: 'Taxes & Licenses',    match: /property tax|business tax|license|permit|franchise tax/i },
  ],
  'sec-curr-assets': [
    { label: 'Accounts Receivable', match: /receivable|\ba\/?r\b/i },
    { label: 'Inventory',           match: /inventory|stock on hand/i },
    { label: 'Prepaid Expenses',    match: /prepaid|deposit/i },
  ],
  'sec-fix-assets': [
    { label: 'Property, Plant & Equipment', match: /equipment|machinery|furniture|computer|vehicle|truck|land|building/i },
    { label: 'Accumulated Depreciation',    match: /accum.*depr|accumulated depreciation/i },
  ],
  'sec-curr-liab': [
    { label: 'Accounts Payable',    match: /payable|\ba\/?p\b/i },
    { label: 'Accrued Expenses',    match: /accrued|payroll liabilit/i },
    { label: 'Other Current Liab',  match: /sales tax payable/i },
  ],
  'sec-long-liab': [
    { label: 'Notes Payable',       match: /note.*payable|long.?term debt|mortgage|equipment loan/i },
  ],
  'sec-equity': [
    { label: 'Owner\'s Equity',     match: /common stock|capital|owner|member|distribution|draws?/i },
    { label: 'Retained Earnings',   match: /retained earnings|prior year/i },
  ],
};

function matchSubHeading(account, sectionId) {
  // First, try matching by QBO account type/sub-type (most reliable)
  const qboType = (account.qbo_type || '').toLowerCase();
  if (sectionId === 'sec-curr-assets') {
    if (qboType.includes('accounts receivable')) return 'Accounts Receivable';
    if (qboType.includes('inventory') || qboType.includes('stock')) return 'Inventory';
    if (qboType.includes('prepaid')) return 'Prepaid Expenses';
  }
  if (sectionId === 'sec-curr-liab') {
    if (qboType.includes('accounts payable')) return 'Accounts Payable';
  }
  if (sectionId === 'sec-fix-assets') {
    if (qboType.includes('accumulated depreciation')) return 'Accumulated Depreciation';
  }

  // Fallback: match by account name patterns
  const rules = SUB_HEADING_RULES[sectionId];
  if (!rules) return null;
  const name = account.name || '';
  for (const rule of rules) {
    if (rule.match.test(name)) return rule.label;
  }
  return null;
}

function buildInitialRows(accounts) {
  // 1. Classify each account into a section
  // 2. Within each section, group by sub-heading pattern matches
  // 3. Create heading rows for groups with 2+ accounts; leave singletons bare under section
  const accountsBySectionSubhead = {}; // { sectionId: { subLabel: [accounts] } }
  for (const acc of accounts) {
    const sectionId = classifyAccount(acc);
    const subLabel = matchSubHeading(acc, sectionId) || '__bare__';
    if (!accountsBySectionSubhead[sectionId]) accountsBySectionSubhead[sectionId] = {};
    if (!accountsBySectionSubhead[sectionId][subLabel]) accountsBySectionSubhead[sectionId][subLabel] = [];
    accountsBySectionSubhead[sectionId][subLabel].push(acc);
  }

  const rows = [];
  let order = 0;
  for (const sectionId of Object.keys(accountsBySectionSubhead)) {
    for (const subLabel of Object.keys(accountsBySectionSubhead[sectionId])) {
      const group = accountsBySectionSubhead[sectionId][subLabel];
      if (subLabel === '__bare__' || group.length < 2) {
        // Place accounts directly under section
        for (const acc of group) {
          rows.push({
            id: `acc-${acc.id}`,
            type: 'account',
            parent: sectionId,
            order: order++,
            label: acc.name,
            accountId: String(acc.id),
            amount: acc.balance || 0,
            priorAmount: acc.prior_balance || 0,
            qboType: acc.qbo_type || acc.type,
            accountType: acc.type,
          });
        }
      } else {
        // Create a sub-heading row and nest the accounts under it
        const headingId = `auto-${sectionId}-${subLabel.replace(/\s+/g, '-').toLowerCase()}`;
        rows.push({
          id: headingId,
          type: 'heading',
          parent: sectionId,
          order: order++,
          label: subLabel,
        });
        let childOrder = 0;
        for (const acc of group) {
          rows.push({
            id: `acc-${acc.id}`,
            type: 'account',
            parent: headingId,
            order: childOrder++,
            label: acc.name,
            accountId: String(acc.id),
            amount: acc.balance || 0,
            priorAmount: acc.prior_balance || 0,
            qboType: acc.qbo_type || acc.type,
            accountType: acc.type,
          });
        }
      }
    }
  }
  return rows;
}

// Convert flat row tree to legacy mapping shape: { income: [{label, accounts:[]}], ... }
function rowsToMapping(rows, allSections) {
  const mapping = {};
  for (const sec of allSections) {
    if (!mapping[sec.mappingKey]) mapping[sec.mappingKey] = [];
  }
  const sortByOrder = (a, b) => a.order - b.order;

  // Recursive collector — gathers all account IDs anywhere under a parent row,
  // including nested sub-headings.
  const collectAccountIds = (parentId) => {
    const ids = [];
    const stack = [parentId];
    while (stack.length) {
      const pid = stack.pop();
      for (const r of rows) {
        if (r.parent !== pid) continue;
        if (r.type === 'account') ids.push(r.accountId);
        else if (r.type === 'heading') stack.push(r.id);
      }
    }
    return ids;
  };

  for (const sec of allSections) {
    // Get direct children of this section in order
    const directChildren = rows.filter(r => r.parent === sec.id).sort(sortByOrder);
    for (const child of directChildren) {
      if (child.type === 'heading') {
        // Heading line: collect ALL nested account descendants
        const accountIds = collectAccountIds(child.id);
        mapping[sec.mappingKey].push({ label: child.label, accounts: accountIds });
      } else if (child.type === 'account') {
        // Bare account directly under section → its own line item
        mapping[sec.mappingKey].push({ label: child.label, accounts: [child.accountId] });
      }
    }
  }
  return mapping;
}

export default function FathomMapLayer({ accounts, mapping: existingMapping, clientId, onComplete, onRefreshFromQBO }) {
  const [tab, setTab] = useState('income_statement');
  const [rows, setRows] = useState(() => buildInitialRows(accounts || []));
  const [search, setSearch] = useState('');
  const [reorderMode, setReorderMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [collapsedSections, setCollapsedSections] = useState(new Set());
  const [addingHeadingFor, setAddingHeadingFor] = useState(null); // section id
  const [newHeadingLabel, setNewHeadingLabel] = useState('');
  const [editingRowId, setEditingRowId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [dragState, setDragState] = useState(null); // { id, draggingIds: [] }
  const [dropIndicator, setDropIndicator] = useState(null); // { rowId, position: 'before'|'after'|'inside' }
  const dragStateRef = useRef(null);
  const dropIndicatorRef = useRef(null);
  const [showMoveToMenu, setShowMoveToMenu] = useState(false);
  const [aiMapping, setAiMapping] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedToast, setSavedToast] = useState(false);
  const [combining, setCombining] = useState(false);
  const [combineLabel, setCombineLabel] = useState('');
  const [sectionDropHighlight, setSectionDropHighlight] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Sort accounts and headings alphabetically within each parent group, preserving hierarchy
  const handleSortAZ = () => {
    userTouchedRef.current = true;
    const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
    // For each parent group, assign new order based on alphabetic sort by label
    const parents = new Set(rows.map(r => r.parent));
    const newRows = [...rows];
    for (const parentId of parents) {
      const siblings = newRows
        .filter(r => r.parent === parentId)
        .sort((a, b) => collator.compare(a.label || '', b.label || ''));
      siblings.forEach((s, idx) => {
        const i = newRows.findIndex(r => r.id === s.id);
        if (i >= 0) newRows[i] = { ...newRows[i], order: idx };
      });
    }
    setRows(newRows);
  };

  // Move a single row up/down within its parent group, or jump to top/bottom
  const moveRow = (rowId, direction) => {
    const row = rows.find(r => r.id === rowId);
    if (!row) return;
    userTouchedRef.current = true;
    const siblings = rows
      .filter(r => r.parent === row.parent)
      .sort((a, b) => a.order - b.order);
    const idx = siblings.findIndex(s => s.id === rowId);
    if (idx < 0) return;

    let newIdx = idx;
    if (direction === 'up') newIdx = Math.max(0, idx - 1);
    else if (direction === 'down') newIdx = Math.min(siblings.length - 1, idx + 1);
    else if (direction === 'top') newIdx = 0;
    else if (direction === 'bottom') newIdx = siblings.length - 1;
    if (newIdx === idx) return;

    const reordered = [...siblings];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(newIdx, 0, moved);

    const orderMap = {};
    reordered.forEach((s, i) => { orderMap[s.id] = i; });
    setRows(rows.map(r => orderMap[r.id] !== undefined ? { ...r, order: orderMap[r.id] } : r));
  };

  // Rebuild the entire tree from scratch using current QBO accounts + latest grouping rules
  const handleResetGrouping = () => {
    if (!accounts || accounts.length === 0) {
      setShowResetConfirm(false);
      return;
    }
    userTouchedRef.current = true;
    setRows(buildInitialRows(accounts));
    setShowResetConfirm(false);
  };
  const [hasLoadedSaved, setHasLoadedSaved] = useState(false);
  const saveTimerRef = useRef(null);
  // Tracks the structural signature of the last successfully saved (or
  // hydrated) tree. Auto-save skips writes whose signature matches.
  const lastSavedSigRef = useRef(null);
  // V19 Pass B (2026-06-11) — structural-signature mount guard.
  // Captures the raw savedTree signature (BEFORE the hydrate
  // reconciler appends new accounts / drops stale accounts / etc.).
  // If the hydrated rows DIVERGE from this baseline, subsequent
  // autosaves are blocked — we will not silently persist the
  // reconciliation diff to coa_mappings. The user must take an
  // explicit action that either accepts the divergence (e.g. clicks
  // Save) or resolves it manually. This is the canonical mapping
  // immutability guard required by the P0 incident postmortem.
  const savedBaselineSigRef = useRef(null);
  const hydrateDivergedRef = useRef(false);
  // Tracks whether the user has actually performed a data-mutating action
  // in this layer (drag-drop move, combine, rename heading, delete heading,
  // sort A–Z, reset grouping, AI refine, bulk Move-to, up/down reorder).
  // Set ONLY inside explicit mutation handlers — NEVER on benign clicks,
  // tab switches, search input, or section collapse. The hydrate-time
  // reconcile (load useEffect) does its own read-modify-write — drops empty
  // headings, reclassifies accounts whose saved section is incompatible
  // with the live QBO type — and the result is non-deterministic across
  // runs. Without this gate, simply mounting the layer (or any benign
  // interaction) was triggering autosave (a P0 boundary violation; see
  // /app/memory/mapping_forensics/POSTMORTEM.md §3).
  const userTouchedRef = useRef(false);

  // V19 Pass B (2026-06-11) — defined ABOVE the load useEffect so it
  // can capture the savedTree baseline signature before the hydrate
  // reconciler runs. See the structural-signature mount guard below.
  // `g` = disclosure_tag (V19 Pass B, intentionally part of the structural
  // signature so tag-only edits fire the autosave path).
  const structuralSignature = (rs) => JSON.stringify(
    (rs || []).map(r => ({
      i: r.id, t: r.type, p: r.parent, o: r.order, l: r.label,
      a: r.accountId, g: r.disclosure_tag || null,
    })).sort((a, b) => (a.i || '').localeCompare(b.i || ''))
  );

  // Load saved tree from backend, OR fall back to auto-bucketing
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!clientId || clientId === 'demo' || !accounts || accounts.length === 0) {
        if (accounts && accounts.length > 0) setRows(buildInitialRows(accounts));
        setHasLoadedSaved(true);
        return;
      }
      try {
        const res = await mappingsAPI.get(clientId);
        const savedTree = res.data?.tree;
        if (savedTree && Array.isArray(savedTree) && savedTree.length > 0) {
          // V19 Pass B — capture the canonical baseline signature
          // BEFORE the reconciler mutates the tree shape. If the
          // hydrated rows diverge from this, subsequent autosaves
          // are blocked (see autosave useEffect below).
          savedBaselineSigRef.current = structuralSignature(savedTree);
          // Reconcile: keep saved headings/order, refresh account labels+amounts from latest sync,
          // purge any stale accounts that no longer exist in QBO, and add any NEW accounts.
          const liveAccountById = new Map(accounts.map(a => [String(a.id), a]));
          const savedAccountIds = new Set(
            savedTree.filter(r => r.type === 'account').map(r => r.accountId)
          );
          // Step 1: drop saved-tree accounts that are no longer in the live QBO accounts list
          let merged = savedTree.filter(r => {
            if (r.type !== 'account') return true;
            return liveAccountById.has(String(r.accountId));
          });
          // Step 2: refresh remaining account labels + amounts from fresh data.
          //
          // V19 (2026-06-10) — Rule 1 enforcement (Ashley Williams /
          // SMJ contra revert family). The previous behavior
          // hard-rerouted any account whose saved canonical section
          // was incompatible with the current QBO type
          // (`SECTION_ALLOWED_TYPES`), then autosaved the corrupted
          // tree forging the staff user's signature. That logic is
          // architecturally wrong: cross-section reclassification is
          // BY DEFINITION a canonical override of the QBO type's
          // natural section (CPA-standard contra presentation). The
          // canonical mapping is the law; the QBO type is advisory.
          //
          // No existing account ever moves sections on mount. Only an
          // explicit user action moves an account. New accounts (Step 4
          // below) still auto-bucket — that is the only auto-
          // classification path that survives.
          merged = merged.map(r => {
            if (r.type !== 'account') return r;
            const fresh = liveAccountById.get(String(r.accountId));
            if (!fresh) return r;
            return {
              ...r,
              label: fresh.name,
              amount: fresh.balance || 0,
              priorAmount: fresh.prior_balance || 0,
              qboType: fresh.qbo_type || fresh.type,
              accountType: fresh.type,
              // r.parent intentionally UNCHANGED — canonical placement
              // is sacred (Rule 1). Removed pre-V19 hard-reroute via
              // classifyAccount(fresh).
            };
          });
          // Step 3: drop now-empty headings (no account descendants)
          const hasAccountDescendant = (parentId) => {
            const kids = merged.filter(r => r.parent === parentId);
            for (const k of kids) {
              if (k.type === 'account') return true;
              if (k.type === 'heading' && hasAccountDescendant(k.id)) return true;
            }
            return false;
          };
          merged = merged.filter(r => r.type !== 'heading' || hasAccountDescendant(r.id));
          // Step 4: append any brand-new accounts not yet in the saved tree
          let order = (savedTree.length > 0 ? Math.max(...savedTree.map(r => r.order || 0)) : 0) + 1;
          for (const acc of accounts) {
            if (!savedAccountIds.has(String(acc.id))) {
              merged.push({
                id: `acc-${acc.id}`,
                type: 'account',
                parent: classifyAccount(acc),
                order: order++,
                label: acc.name,
                accountId: String(acc.id),
                amount: acc.balance || 0,
                priorAmount: acc.prior_balance || 0,
                qboType: acc.qbo_type || acc.type,
                accountType: acc.type,
              });
            }
          }
          if (!cancelled) {
            setRows(merged);
            // V19 Pass B mount guard — compare the reconciled tree
            // against the saved baseline signature captured BEFORE
            // reconciliation. If they diverge, the hydrate logic
            // mutated the canonical mapping (e.g. dropped an empty
            // heading, refreshed a label, appended a new account).
            // Block all subsequent autosaves so we never silently
            // persist hydrate-time mutations to coa_mappings. Only
            // an explicit user action that triggers an explicit
            // save (e.g. clicking Save in the editor) will overwrite
            // the baseline.
            const mergedSig = structuralSignature(merged);
            if (savedBaselineSigRef.current !== null
                && mergedSig !== savedBaselineSigRef.current) {
              hydrateDivergedRef.current = true;
              // eslint-disable-next-line no-console
              console.warn(
                '[FathomMapLayer] Hydrate diverged from saved baseline — ',
                'autosave BLOCKED until explicit user action. ',
                'baseline=', savedBaselineSigRef.current.length,
                'chars  merged=', mergedSig.length, 'chars',
              );
            }
          }
        } else {
          if (!cancelled) setRows(buildInitialRows(accounts));
        }
      } catch (err) {
        console.warn('Failed to load saved mapping; using auto-bucket:', err);
        if (!cancelled) setRows(buildInitialRows(accounts));
      } finally {
        if (!cancelled) setHasLoadedSaved(true);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, accounts]);

  // Auto-save the tree whenever it CHANGES STRUCTURALLY. We compare a
  // stable signature of (id, type, parent, order, label, accountId) —
  // ignoring volatile `amount`/`priorAmount` fields which the hydrate
  // logic refreshes from the latest QBO sync on every mount. Without
  // this filter, simply opening the page wrote to coa_mappings (a P0
  // boundary violation; see /app/memory/mapping_forensics/POSTMORTEM.md).
  // structuralSignature is defined above (V19 Pass B refactor).
  useEffect(() => {
    if (!hasLoadedSaved || !clientId || clientId === 'demo' || rows.length === 0) return;
    const sig = structuralSignature(rows);
    // First time after hydrate: capture the signature, do NOT save.
    if (lastSavedSigRef.current === null) {
      lastSavedSigRef.current = sig;
      return;
    }
    // No structural change since last save: skip.
    if (sig === lastSavedSigRef.current) return;
    // User hasn't actually interacted with the layer yet — this rows
    // change came from the hydrate reconcile, not a human edit. Refresh
    // the signature so we don't flag it as dirty later, but DO NOT write.
    if (!userTouchedRef.current) {
      lastSavedSigRef.current = sig;
      return;
    }
    // V19 Pass B mount guard — block autosave if the hydrate
    // reconcile diverged the tree from the saved baseline. Even
    // though userTouchedRef is now true, we refuse to silently
    // ship the reconciliation diff. A future enhancement can add
    // a UI affordance ("Accept hydrate changes and save") to clear
    // this flag explicitly.
    if (hydrateDivergedRef.current) {
      // eslint-disable-next-line no-console
      console.warn(
        '[FathomMapLayer] Autosave BLOCKED — hydrate reconcile diverged ',
        'from saved baseline. Use the staff Mapping History modal to ',
        'review prior state, or perform an explicit Save to accept ',
        'the divergence.',
      );
      return;
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        const mapping = rowsToMapping(rows, [...SECTIONS.income_statement, ...SECTIONS.balance_sheet]);
        await mappingsAPI.save(clientId, mapping, rows);
        lastSavedSigRef.current = sig;
        setSavedToast(true);
        setTimeout(() => setSavedToast(false), 1400);
      } catch (err) {
        console.warn('Auto-save failed:', err);
      }
    }, 900);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, hasLoadedSaved, clientId]);

  const activeSections = SECTIONS[tab];
  const allSections = [...SECTIONS.income_statement, ...SECTIONS.balance_sheet];

  // Filter rows by search query
  const matchesSearch = (row) => {
    if (!search) return true;
    return row.label.toLowerCase().includes(search.toLowerCase());
  };

  // Compute the visible tree for the active tab
  const visibleRows = useMemo(() => {
    const sectionIds = new Set(activeSections.map(s => s.id));
    return rows.filter(r => {
      let cur = r;
      while (cur && cur.parent) {
        if (sectionIds.has(cur.parent)) return true;
        cur = rows.find(x => x.id === cur.parent);
      }
      return false;
    });
  }, [rows, activeSections]);

  // Compute subtotal for a section / heading by recursively summing account amounts.
  // We apply the SECTION's natural display sign at the leaf level: backend now returns
  // natural debit-credit signed balances (positive for debit, negative for credit).
  // For "credit-natural" sections (revenue, liabilities, equity, other income), we
  // flip the sign so users see positive numbers — matching standard CPA presentation.
  const findEnclosingSection = (parentId) => {
    let cur = parentId;
    let guard = 0;
    while (cur && guard++ < 50) {
      if (SECTION_DISPLAY_SIGN[cur] !== undefined) return cur;
      const parentRow = rows.find(x => x.id === cur);
      if (!parentRow) return null;
      cur = parentRow.parent;
    }
    return null;
  };

  const subtotalFor = (parentId) => {
    let total = 0;
    const walk = (pid) => {
      for (const r of rows) {
        if (r.parent !== pid) continue;
        if (r.type === 'account') {
          const sec = findEnclosingSection(r.parent);
          const sign = sec ? (SECTION_DISPLAY_SIGN[sec] || 1) : 1;
          total += sign * (r.amount || 0);
        } else {
          walk(r.id);
        }
      }
    };
    walk(parentId);
    return total;
  };

  // Display value for a single account row, with section's natural sign applied.
  const displayAmountFor = (row) => {
    if (row.type !== 'account') return subtotalFor(row.id);
    const sec = findEnclosingSection(row.parent);
    const sign = sec ? (SECTION_DISPLAY_SIGN[sec] || 1) : 1;
    return sign * (row.amount || 0);
  };

  const toggleSection = (sectionId) => {
    const next = new Set(collapsedSections);
    if (next.has(sectionId)) next.delete(sectionId);
    else next.add(sectionId);
    setCollapsedSections(next);
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleAddHeading = (sectionId) => {
    if (!newHeadingLabel.trim()) {
      setAddingHeadingFor(null);
      return;
    }
    userTouchedRef.current = true;
    const id = `head-${Date.now()}`;
    // Insert as first child under section
    const sectionChildren = rows.filter(r => r.parent === sectionId);
    const minOrder = sectionChildren.length > 0 ? Math.min(...sectionChildren.map(r => r.order)) : 0;
    const newRow = {
      id,
      type: 'heading',
      parent: sectionId,
      order: minOrder - 1,
      label: newHeadingLabel.trim(),
    };
    setRows([...rows, newRow]);
    setNewHeadingLabel('');
    setAddingHeadingFor(null);
  };

  const handleStartEdit = (row) => {
    setEditingRowId(row.id);
    setEditLabel(row.label);
  };

  const handleSaveEdit = () => {
    if (!editingRowId || !editLabel.trim()) {
      setEditingRowId(null);
      return;
    }
    userTouchedRef.current = true;
    setRows(rows.map(r => r.id === editingRowId ? { ...r, label: editLabel.trim() } : r));
    setEditingRowId(null);
  };

  const handleDelete = (rowId) => {
    const row = rows.find(r => r.id === rowId);
    if (!row) return;
    if (row.type === 'heading') {
      userTouchedRef.current = true;
      // Move children up to the section, delete the heading
      const section = row.parent;
      setRows(rows
        .filter(r => r.id !== rowId)
        .map(r => r.parent === rowId ? { ...r, parent: section } : r)
      );
    }
  };

  // V19 Pass B (2026-06-11) — disclosure_tag setter for the per-account
  // dropdown. Only valid on account-type rows; the four enum values are
  // ALSO enforced server-side (see server.save_client_mapping). A `null`
  // tag means "no disclosure significance" and is the default for every
  // account leaf — staff must opt in explicitly per CPA judgment.
  // Switching the tag flips userTouchedRef so the autosave fires; the
  // structural signature includes `disclosure_tag` (see structuralSignature
  // above) so a tag-only change is treated as a real structural mutation
  // for save-detection purposes.
  const handleSetDisclosureTag = (rowId, tag) => {
    const row = rows.find(r => r.id === rowId);
    if (!row || row.type !== 'account') return;
    const next = tag === '' ? null : tag;
    if ((row.disclosure_tag || null) === next) return;
    userTouchedRef.current = true;
    setRows(rows.map(r => r.id === rowId ? { ...r, disclosure_tag: next } : r));
  };

  // === Drag-and-drop handlers ===
  const onDragStart = (e, rowId) => {
    const draggingIds = selectedIds.has(rowId) ? Array.from(selectedIds) : [rowId];
    const state = { id: rowId, draggingIds };
    dragStateRef.current = state;
    setDragState(state);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', rowId);
    // V16 fix — register cleanup on `dragend` ONLY, and use bubble phase
    // (useCapture=false). Earlier the cleanup also listened for `drop`
    // in capture phase, which ran BEFORE the row-level onDrop handler
    // and wiped dragStateRef → onDropOnRow saw ds=null and silently
    // bailed out. `dragend` always fires after `drop` so we still get
    // teardown if the user releases outside any drop zone.
    const endHandler = () => {
      stopAutoScroll();
      setDropIndicator(null);
      setDragState(null);
      dragStateRef.current = null;
      dropIndicatorRef.current = null;
      window.removeEventListener('dragend', endHandler);
    };
    window.addEventListener('dragend', endHandler);
  };

  const scrollRafRef = useRef(null);
  const scrollVelocityRef = useRef(0);

  // Auto-scroll loop while dragging near viewport edges
  const startAutoScroll = () => {
    if (scrollRafRef.current) return;
    const step = () => {
      const v = scrollVelocityRef.current;
      if (v !== 0) {
        window.scrollBy({ top: v, behavior: 'instant' });
      }
      scrollRafRef.current = requestAnimationFrame(step);
    };
    scrollRafRef.current = requestAnimationFrame(step);
  };

  const stopAutoScroll = () => {
    scrollVelocityRef.current = 0;
    if (scrollRafRef.current) {
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }
  };

  const updateScrollVelocity = (clientY) => {
    const EDGE = 90;          // px from viewport edge that triggers scroll
    const MAX_SPEED = 22;     // px per frame at max
    const vh = window.innerHeight;
    if (clientY < EDGE) {
      const ratio = (EDGE - clientY) / EDGE;
      scrollVelocityRef.current = -Math.max(4, MAX_SPEED * ratio);
    } else if (clientY > vh - EDGE) {
      const ratio = (clientY - (vh - EDGE)) / EDGE;
      scrollVelocityRef.current = Math.max(4, MAX_SPEED * ratio);
    } else {
      scrollVelocityRef.current = 0;
    }
  };

  const onDragOver = (e, targetRowId, position) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    updateScrollVelocity(e.clientY);
    startAutoScroll();
    const ds = dragStateRef.current || dragState;
    if (!ds || ds.draggingIds.includes(targetRowId)) return;
    const indicator = { rowId: targetRowId, position };
    dropIndicatorRef.current = indicator;
    setDropIndicator(indicator);
  };

  const onDragLeave = () => {
    // small delay-free clear; onDragOver of next target will reset
  };

  const clearDragRefs = () => {
    dragStateRef.current = null;
    dropIndicatorRef.current = null;
    stopAutoScroll();
  };

  const performMove = (draggingIds, targetParentId, insertBeforeId = null, insertAfterId = null) => {
    userTouchedRef.current = true;
    // Compute new orders. For simplicity: order is 'time' (use Date.now offsets)
    const targetChildren = rows
      .filter(r => r.parent === targetParentId && !draggingIds.includes(r.id))
      .sort((a, b) => a.order - b.order);

    let insertIndex = targetChildren.length; // default: append to end
    if (insertBeforeId) {
      const idx = targetChildren.findIndex(c => c.id === insertBeforeId);
      if (idx >= 0) insertIndex = idx;
    } else if (insertAfterId) {
      const idx = targetChildren.findIndex(c => c.id === insertAfterId);
      if (idx >= 0) insertIndex = idx + 1;
    }

    const newRows = rows.map(r => {
      if (!draggingIds.includes(r.id)) return r;
      return { ...r, parent: targetParentId };
    });

    // Reorder: rebuild target section's children sequence
    const targetMoved = newRows.filter(r => draggingIds.includes(r.id));
    const baseChildren = newRows
      .filter(r => r.parent === targetParentId && !draggingIds.includes(r.id))
      .sort((a, b) => a.order - b.order);

    const finalSequence = [
      ...baseChildren.slice(0, insertIndex),
      ...targetMoved,
      ...baseChildren.slice(insertIndex),
    ];

    let n = 0;
    const orderMap = {};
    for (const r of finalSequence) orderMap[r.id] = n++;

    setRows(newRows.map(r => orderMap[r.id] !== undefined ? { ...r, order: orderMap[r.id] } : r));
    clearSelection();
    clearDragRefs();
    setDragState(null);
    setDropIndicator(null);
  };

  const onDropOnRow = (e, targetRowId, rowElement) => {
    e.preventDefault();
    e.stopPropagation();
    const ds = dragStateRef.current || dragState;
    if (!ds) return;
    const targetRow = rows.find(r => r.id === targetRowId);
    if (!targetRow) return;
    if (ds.draggingIds.includes(targetRowId)) return;

    // ALWAYS compute position from the actual drop coordinates (don't trust dragOver state).
    let position = 'after';
    if (rowElement) {
      const rect = rowElement.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const h = rect.height;
      if (targetRow.type === 'heading') {
        if (y < h * 0.25) position = 'before';
        else if (y > h * 0.75) position = 'after';
        else position = 'inside';
      } else {
        // Account row: 3 zones — top third = before, middle = combine, bottom third = after
        if (y < h * 0.33) position = 'before';
        else if (y > h * 0.67) position = 'after';
        else position = 'combine-with-account';
      }
    }

    if (position === 'inside' && (targetRow.type === 'heading' || targetRow.type === 'section')) {
      performMove(ds.draggingIds, targetRow.id, null, null);
    } else if (position === 'combine-with-account' && targetRow.type === 'account') {
      combineWithAccount(ds.draggingIds, targetRow);
    } else {
      const parentId = targetRow.parent;
      if (!parentId) return;
      if (position === 'before') {
        performMove(ds.draggingIds, parentId, targetRowId, null);
      } else {
        performMove(ds.draggingIds, parentId, null, targetRowId);
      }
    }
  };

  // Combine the dragged accounts with a target account, creating an auto-heading
  const combineWithAccount = (draggedIds, targetAccount) => {
    userTouchedRef.current = true;
    const parentId = targetAccount.parent;
    const headingId = `head-${Date.now()}`;
    const targetOrder = targetAccount.order;

    // Heading takes target account's label by default; user can rename it
    const newHeading = {
      id: headingId,
      type: 'heading',
      parent: parentId,
      order: targetOrder,
      label: targetAccount.label,
    };

    // Move target account + dragged accounts under the new heading
    const moveSet = new Set([targetAccount.id, ...draggedIds]);
    let childOrder = 0;
    const orderingFn = (id) => {
      // Preserve dragged order: target first, then dragged accounts in their original order
      if (id === targetAccount.id) return 0;
      const idx = draggedIds.indexOf(id);
      return idx >= 0 ? idx + 1 : 99;
    };

    const updatedRows = [
      ...rows.map(r => {
        if (moveSet.has(r.id)) {
          return { ...r, parent: headingId, order: orderingFn(r.id) };
        }
        return r;
      }),
      newHeading,
    ];
    setRows(updatedRows);
    clearSelection();
    clearDragRefs();
    setDragState(null);
    setDropIndicator(null);
  };

  const onDropOnSection = (e, sectionId) => {
    e.preventDefault();
    e.stopPropagation();
    const ds = dragStateRef.current || dragState;
    if (!ds) return;
    // Dropping on the section header itself means "put at the top of this section".
    // The first child (if any) becomes our insertBefore anchor.
    const sectionChildren = rows
      .filter(r => r.parent === sectionId && !ds.draggingIds.includes(r.id))
      .sort((a, b) => a.order - b.order);
    const firstChildId = sectionChildren.length > 0 ? sectionChildren[0].id : null;
    performMove(ds.draggingIds, sectionId, firstChildId, null);
  };

  // === Move To bulk action ===
  const handleMoveTo = (targetSectionId) => {
    if (selectedIds.size === 0) return;
    performMove(Array.from(selectedIds), targetSectionId);
    setShowMoveToMenu(false);
  };

  // === Combine selected accounts into a new heading line ===
  const handleCombineSelected = () => {
    if (!combineLabel.trim() || selectedIds.size === 0) return;
    const selectedRows = rows.filter(r => selectedIds.has(r.id) && r.type === 'account');
    if (selectedRows.length === 0) return;
    userTouchedRef.current = true;

    // Place the new heading inside the FIRST selected account's parent section.
    // Walk up the tree to find the section.
    const findSection = (rowId) => {
      let cur = rows.find(r => r.id === rowId);
      while (cur && cur.parent) {
        const parent = rows.find(r => r.id === cur.parent);
        if (!parent) break;
        if (parent.type === 'section') return parent.id;
        cur = parent;
      }
      // If parent itself is a section id (no row object for sections)
      const sectionIds = allSections.map(s => s.id);
      cur = rows.find(r => r.id === rowId);
      if (cur && sectionIds.includes(cur.parent)) return cur.parent;
      return null;
    };

    let sectionId = findSection(selectedRows[0].id);
    if (!sectionId) {
      const sectionIds = allSections.map(s => s.id);
      if (sectionIds.includes(selectedRows[0].parent)) sectionId = selectedRows[0].parent;
    }
    if (!sectionId) return;

    const headingId = `head-${Date.now()}`;
    const selectedIdList = Array.from(selectedIds);

    // Add heading row + move selected accounts under it
    const sectionChildren = rows.filter(r => r.parent === sectionId && !selectedIdList.includes(r.id));
    const insertOrder = sectionChildren.length > 0 ? Math.min(...sectionChildren.map(r => r.order)) - 1 : 0;

    const newHeading = {
      id: headingId,
      type: 'heading',
      parent: sectionId,
      order: insertOrder,
      label: combineLabel.trim(),
    };

    const updatedRows = [
      ...rows.map(r => selectedIdList.includes(r.id) ? { ...r, parent: headingId } : r),
      newHeading,
    ];
    setRows(updatedRows);

    setCombineLabel('');
    setCombining(false);
    clearSelection();
  };

  // === AI Refine ===
  const handleAIRefine = async () => {
    setAiMapping(true);
    userTouchedRef.current = true;
    try {
      // Build a coarse template from current state to let AI suggest better groupings
      const templateForAI = {};
      for (const sec of allSections) {
        const headings = rows.filter(r => r.parent === sec.id && r.type === 'heading');
        if (headings.length > 0) {
          templateForAI[sec.label] = headings.map(h => ({ label: h.label, accounts: [] }));
        }
      }
      const res = await aiAPI.mapAccounts(accounts, templateForAI);
      if (res.data?.success && res.data.suggested_mapping) {
        // Apply: for each heading label, move matching accounts under it
        const suggested = res.data.suggested_mapping;
        let newRows = [...rows];
        for (const sec of allSections) {
          const headings = newRows.filter(r => r.parent === sec.id && r.type === 'heading');
          for (const h of headings) {
            const accountIds = suggested[h.label] || [];
            for (const accId of accountIds) {
              const rowId = `acc-${accId}`;
              const target = newRows.find(r => r.id === rowId);
              if (target) {
                newRows = newRows.map(r => r.id === rowId ? { ...r, parent: h.id } : r);
              }
            }
          }
        }
        setRows(newRows);
      }
    } catch (err) {
      console.error('AI refine failed:', err);
    } finally {
      setAiMapping(false);
    }
  };

  const handleContinue = async () => {
    setSaving(true);
    const mapping = rowsToMapping(rows, allSections);
    try {
      await mappingsAPI.save(clientId, mapping, rows);
    } catch (err) {
      console.error('Failed to save mapping:', err);
    }
    setSaving(false);
    onComplete(mapping);
  };

  return (
    <div
      data-testid="fathom-map-layer"
      style={{ maxWidth: 1200, margin: '0 auto', position: 'relative' }}
    >
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>Step 2 of 3</div>
        <h2 className="font-heading" style={{ fontSize: 28, fontWeight: 500, color: COLORS.dark, marginBottom: 6 }}>
          Chart of Accounts
        </h2>
        <p style={{ color: COLORS.muted, fontSize: 14 }}>
          Accounts are auto-grouped into sub-headings from QuickBooks. <strong>Drag an account onto a heading to combine</strong>, or drop between accounts to reorder. Click <em>+ Add Heading</em> to create a new group.
        </p>
      </div>

      <MappingRepairPanel clientId={clientId} onApplied={() => window.location.reload()} />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${COLORS.border}`, marginBottom: 20 }}>
        {[
          { id: 'income_statement', label: 'Income Statement' },
          { id: 'balance_sheet', label: 'Balance Sheet' },
        ].map(t => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => { setTab(t.id); clearSelection(); }}
            style={{
              padding: '10px 18px',
              background: 'transparent',
              border: 'none',
              borderBottom: tab === t.id ? `2px solid ${COLORS.primary}` : '2px solid transparent',
              color: tab === t.id ? COLORS.primary : COLORS.muted,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '0 1 320px' }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: COLORS.muted }} />
          <input
            data-testid="coa-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search accounts..."
            style={{
              width: '100%',
              padding: '8px 12px 8px 34px',
              borderRadius: 8,
              border: `1px solid ${COLORS.border}`,
              fontSize: 13,
              background: '#fff',
            }}
          />
        </div>

        <button
          data-testid="reorder-toggle"
          onClick={() => { setReorderMode(!reorderMode); clearSelection(); }}
          style={{
            padding: '8px 14px',
            borderRadius: 8,
            border: `1px solid ${reorderMode ? COLORS.primary : COLORS.border}`,
            background: reorderMode ? COLORS.primary : '#fff',
            color: reorderMode ? '#fff' : COLORS.dark,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Move size={13} /> {reorderMode ? 'Done Reordering' : 'Reorder'}
        </button>

        <button
          data-testid="sort-az-btn"
          onClick={handleSortAZ}
          title="Sort accounts alphabetically within each group"
          style={{
            padding: '8px 14px',
            borderRadius: 8,
            border: `1px solid ${COLORS.border}`,
            background: '#fff',
            color: COLORS.dark,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <ArrowDownAZ size={13} /> Sort A–Z
        </button>

        <button
          data-testid="reset-grouping-btn"
          onClick={() => setShowResetConfirm(true)}
          title="Rebuild the chart using current QuickBooks types and auto-groupings (preserves balances, discards manual layout)"
          style={{
            padding: '8px 14px',
            borderRadius: 8,
            border: `1px solid ${COLORS.border}`,
            background: '#fff',
            color: COLORS.dark,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <RotateCcw size={13} /> Reset Grouping
        </button>

        <button
          data-testid="ai-refine-btn"
          onClick={handleAIRefine}
          disabled={aiMapping}
          style={{
            marginLeft: 'auto',
            padding: '8px 14px',
            borderRadius: 8,
            border: 'none',
            background: aiMapping ? COLORS.muted : 'linear-gradient(135deg, #8B5CF6, #6366F1)',
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
            cursor: aiMapping ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            boxShadow: '0 2px 8px rgba(99,102,241,0.25)',
          }}
        >
          {aiMapping ? <><Loader2 size={13} className="animate-spin" /> Refining…</> : <><Sparkles size={13} /> AI Refine Groupings</>}
        </button>
      </div>

      {/* Selection action bar */}
      {selectedIds.size > 0 && (
        <div
          data-testid="bulk-action-bar"
          style={{
            position: 'sticky',
            top: 8,
            zIndex: 10,
            padding: '10px 16px',
            borderRadius: 10,
            background: COLORS.dark,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 12,
            boxShadow: '0 6px 16px rgba(15,25,35,0.18)',
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600 }}>{selectedIds.size} selected</span>

          {/* Combine — primary action */}
          {!combining ? (
            <button
              data-testid="combine-btn"
              onClick={() => { setCombining(true); setShowMoveToMenu(false); }}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                border: 'none',
                background: COLORS.primary,
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Plus size={12} /> Combine into new line
            </button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                autoFocus
                data-testid="combine-input"
                type="text"
                value={combineLabel}
                onChange={(e) => setCombineLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCombineSelected();
                  if (e.key === 'Escape') { setCombining(false); setCombineLabel(''); }
                }}
                placeholder="New line name (e.g., Repairs & Maintenance)"
                style={{
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: '1px solid rgba(255,255,255,0.4)',
                  background: 'rgba(255,255,255,0.1)',
                  color: '#fff',
                  fontSize: 12,
                  width: 280,
                }}
              />
              <button
                data-testid="combine-save"
                onClick={handleCombineSelected}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: 'none',
                  background: COLORS.success,
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Combine
              </button>
              <button
                onClick={() => { setCombining(false); setCombineLabel(''); }}
                style={{
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: '1px solid rgba(255,255,255,0.25)',
                  background: 'transparent',
                  color: '#fff',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          )}

          <div style={{ position: 'relative' }}>
            <button
              data-testid="move-to-btn"
              onClick={() => setShowMoveToMenu(!showMoveToMenu)}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: '1px solid rgba(255,255,255,0.25)',
                background: 'transparent',
                color: '#fff',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              Move to <ChevronDown size={12} />
            </button>
            {showMoveToMenu && (
              <div style={{
                position: 'absolute',
                top: '110%',
                left: 0,
                background: '#fff',
                color: COLORS.dark,
                borderRadius: 8,
                padding: 6,
                minWidth: 220,
                boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
                border: `1px solid ${COLORS.border}`,
                maxHeight: 320,
                overflowY: 'auto',
              }}>
                {allSections.map(sec => (
                  <button
                    key={sec.id}
                    onClick={() => handleMoveTo(sec.id)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 10px',
                      border: 'none',
                      background: 'transparent',
                      fontSize: 13,
                      cursor: 'pointer',
                      borderRadius: 4,
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = COLORS.subtle}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    {sec.label}
                  </button>
                ))}
                {/* Allow moving under any existing heading */}
                {rows.filter(r => r.type === 'heading').length > 0 && (
                  <div style={{ borderTop: `1px solid ${COLORS.border}`, marginTop: 6, paddingTop: 6 }}>
                    <div style={{ fontSize: 10, color: COLORS.muted, padding: '4px 10px', textTransform: 'uppercase', fontWeight: 700 }}>Existing Groups</div>
                    {rows.filter(r => r.type === 'heading').map(h => (
                      <button
                        key={h.id}
                        onClick={() => handleMoveTo(h.id)}
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          padding: '8px 10px',
                          border: 'none',
                          background: 'transparent',
                          fontSize: 13,
                          cursor: 'pointer',
                          borderRadius: 4,
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = COLORS.subtle}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        ↳ {h.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <button onClick={clearSelection} style={{
            marginLeft: 'auto',
            padding: '6px 10px',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.25)',
            background: 'transparent',
            color: '#fff',
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
          }}>
            Clear
          </button>
        </div>
      )}

      {/* Sections */}
      <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${COLORS.border}` }}>
        {activeSections.map((sec, secIdx) => {
          const isCollapsed = collapsedSections.has(sec.id);
          const subtotal = subtotalFor(sec.id);
          return (
            <div
              key={sec.id}
              data-testid={`section-${sec.id}`}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => onDropOnSection(e, sec.id)}
              style={{
                borderBottom: secIdx < activeSections.length - 1 ? `1px solid ${COLORS.border}` : 'none',
              }}
            >
              {/* Section header */}
              <div className="coa-section-header" style={{
                display: 'flex',
                alignItems: 'center',
                padding: '14px 18px',
                background: '#FAFBFC',
                cursor: 'pointer',
                gap: 10,
              }}
                onClick={() => toggleSection(sec.id)}
              >
                {isCollapsed ? <ChevronRight size={16} color={COLORS.muted} /> : <ChevronDown size={16} color={COLORS.muted} />}
                <span style={{
                  width: 4,
                  height: 18,
                  background: sec.color,
                  borderRadius: 2,
                }} />
                <span className="coa-section-label" style={{ fontSize: 14, fontWeight: 700, color: COLORS.dark }}>{sec.label}</span>
                <span className="coa-section-actions" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13, fontWeight: 600, color: COLORS.dark }}>
                    {formatCurrency(subtotal)}
                  </span>
                  <button
                    data-testid={`add-heading-${sec.id}`}
                    onClick={(e) => { e.stopPropagation(); setAddingHeadingFor(sec.id); }}
                    style={{
                      padding: '4px 10px',
                      borderRadius: 6,
                      border: `1px solid ${COLORS.border}`,
                      background: '#fff',
                      color: COLORS.muted,
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <Plus size={11} /> Add Heading
                  </button>
                </span>
              </div>

              {/* Add Heading inline editor */}
              {addingHeadingFor === sec.id && (
                <div style={{ padding: '10px 18px 10px 48px', display: 'flex', gap: 8, background: '#F7FAFC' }}>
                  <input
                    autoFocus
                    type="text"
                    value={newHeadingLabel}
                    onChange={(e) => setNewHeadingLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddHeading(sec.id);
                      if (e.key === 'Escape') { setAddingHeadingFor(null); setNewHeadingLabel(''); }
                    }}
                    placeholder="New heading name…"
                    style={{
                      flex: 1,
                      padding: '7px 12px',
                      borderRadius: 6,
                      border: `1px solid ${COLORS.primary}`,
                      fontSize: 13,
                    }}
                  />
                  <button
                    onClick={() => handleAddHeading(sec.id)}
                    style={{
                      padding: '7px 14px',
                      borderRadius: 6,
                      border: 'none',
                      background: COLORS.primary,
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Create
                  </button>
                  <button
                    onClick={() => { setAddingHeadingFor(null); setNewHeadingLabel(''); }}
                    style={{
                      padding: '7px 12px',
                      borderRadius: 6,
                      border: `1px solid ${COLORS.border}`,
                      background: '#fff',
                      color: COLORS.muted,
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}

              {/* Section content */}
              {!isCollapsed && (
                <div>
                  {dragState && (
                    <div
                      data-testid={`top-dropzone-${sec.id}`}
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setSectionDropHighlight(sec.id); }}
                      onDrop={(e) => { e.stopPropagation(); setSectionDropHighlight(null); onDropOnSection(e, sec.id); }}
                      style={{
                        height: 8,
                        margin: '0 18px',
                        marginTop: 2,
                        marginBottom: 2,
                        background: sectionDropHighlight === sec.id ? COLORS.success : 'transparent',
                        borderRadius: 4,
                        transition: 'background 0.1s',
                      }}
                    />
                  )}
                  {flattenTree(rows, sec.id, 1, matchesSearch).map(({ row, depth }) => (
                    <RowItem
                      key={row.id}
                      row={row}
                      depth={depth}
                      rows={rows}
                      subtotalFor={subtotalFor}
                      displayAmountFor={displayAmountFor}
                      reorderMode={reorderMode}
                      selectedIds={selectedIds}
                      toggleSelect={toggleSelect}
                      editingRowId={editingRowId}
                      editLabel={editLabel}
                      setEditLabel={setEditLabel}
                      onStartEdit={handleStartEdit}
                      onSaveEdit={handleSaveEdit}
                      onCancelEdit={() => setEditingRowId(null)}
                      onDelete={handleDelete}
                      onDragStart={onDragStart}
                      onDragOver={onDragOver}
                      onDragLeave={onDragLeave}
                      onDropOnRow={onDropOnRow}
                      moveRow={moveRow}
                      dropIndicator={dropIndicator}
                      dragState={dragState}
                      onSetDisclosureTag={handleSetDisclosureTag}
                    />
                  ))}
                  {rows.filter(r => r.parent === sec.id).length === 0 && (
                    <div style={{ padding: '18px 18px 18px 48px', color: COLORS.muted, fontSize: 13, fontStyle: 'italic' }}>
                      No accounts in this section.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Continue button + auto-save indicator */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 14, marginTop: 24 }}>
        <span
          data-testid="autosave-indicator"
          style={{
            fontSize: 12,
            color: savedToast ? COLORS.success : COLORS.muted,
            opacity: savedToast ? 1 : 0.6,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            transition: 'color 0.2s, opacity 0.2s',
          }}
        >
          {savedToast ? <><Check size={13} /> Saved</> : 'Changes save automatically'}
        </span>
        <button
          data-testid="map-continue-btn"
          onClick={handleContinue}
          disabled={saving}
          style={{
            padding: '12px 28px',
            borderRadius: 10,
            border: 'none',
            background: COLORS.primary,
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            cursor: saving ? 'wait' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : 'Continue to Reports →'}
        </button>
      </div>

      {showResetConfirm && (
        <div
          onClick={() => setShowResetConfirm(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,25,35,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="reset-confirm-modal"
            style={{
              background: '#fff', borderRadius: 14, width: '100%', maxWidth: 460,
              padding: 28, boxShadow: '0 24px 60px rgba(15,25,35,0.25)',
            }}
          >
            <div style={{
              width: 48, height: 48, borderRadius: 12, background: '#FEF3C7',
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
            }}>
              <RotateCcw size={22} color="#D97706" />
            </div>
            <h3 className="font-heading" style={{ fontSize: 20, fontWeight: 600, color: COLORS.dark, marginBottom: 8 }}>
              Reset Chart of Accounts grouping?
            </h3>
            <p style={{ fontSize: 14, color: COLORS.muted, marginBottom: 22, lineHeight: 1.55 }}>
              This rebuilds the layout using current QuickBooks account types and the latest auto-grouping rules. Your account balances stay intact, but any custom headings, renames, or manual ordering you've made will be replaced.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowResetConfirm(false)}
                style={{
                  padding: '9px 18px', borderRadius: 8, border: `1px solid ${COLORS.border}`,
                  background: '#fff', color: COLORS.dark, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                data-testid="confirm-reset-btn"
                onClick={handleResetGrouping}
                style={{
                  padding: '9px 18px', borderRadius: 8, border: 'none',
                  background: '#D97706', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Reset Grouping
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function RowItem({
  row, depth, rows, subtotalFor, displayAmountFor,
  reorderMode, selectedIds, toggleSelect,
  editingRowId, editLabel, setEditLabel, onStartEdit, onSaveEdit, onCancelEdit, onDelete,
  onDragStart, onDragOver, onDragLeave, onDropOnRow, moveRow,
  dropIndicator, dragState,
  onSetDisclosureTag,
}) {
  const rowRef = useRef(null);
  const isSelected = selectedIds.has(row.id);
  const isEditing = editingRowId === row.id;
  const isHeading = row.type === 'heading';
  const indicator = dropIndicator?.rowId === row.id ? dropIndicator.position : null;
  const isDraggingThis = dragState?.draggingIds?.includes(row.id);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!rowRef.current) return;
    const rect = rowRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const h = rect.height;
    let position;
    // Heading row: top 25% = insert before, middle 50% = inside, bottom 25% = insert after
    if (isHeading) {
      if (y < h * 0.25) position = 'before';
      else if (y > h * 0.75) position = 'after';
      else position = 'inside';
    } else {
      // Account row: 3 zones — top 33% before, middle 34% combine, bottom 33% after
      if (y < h * 0.33) position = 'before';
      else if (y > h * 0.67) position = 'after';
      else position = 'combine-with-account';
    }
    onDragOver(e, row.id, position);
  };

  return (
    <>
      {indicator === 'before' && <DropLine />}
      <div
        ref={rowRef}
        data-testid={`row-${row.id}`}
        className={`coa-row ${isHeading ? 'coa-row-heading' : 'coa-row-account'}`}
        draggable={true}
        onDragStart={(e) => onDragStart(e, row.id)}
        onDragOver={handleDragOver}
        onDragLeave={onDragLeave}
        onDrop={(e) => onDropOnRow(e, row.id, rowRef.current)}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '9px 18px',
          paddingLeft: 18 + depth * 28,
          gap: 10,
          borderTop: `1px solid ${COLORS.border}`,
          background: isSelected ? '#EFF6FF' : (indicator === 'inside' || indicator === 'combine-with-account' ? '#ECFCCB' : '#fff'),
          opacity: isDraggingThis ? 0.4 : 1,
          cursor: 'grab',
          transition: 'background 0.08s',
          position: 'relative',
        }}
      >
        {/* Always show checkbox for accounts (and headings in reorder mode) */}
        {(row.type === 'account' || reorderMode) && (
          <input
            type="checkbox"
            data-testid={`select-${row.id}`}
            checked={isSelected}
            onChange={() => toggleSelect(row.id)}
            onClick={(e) => e.stopPropagation()}
            style={{ cursor: 'pointer', accentColor: COLORS.primary }}
          />
        )}
        {reorderMode && (
          <GripVertical size={14} color={COLORS.muted} />
        )}

        {isHeading && !reorderMode && <ChevronDown size={13} color={COLORS.muted} />}

        {isEditing ? (
          <>
            <input
              autoFocus
              type="text"
              value={editLabel}
              onChange={(e) => setEditLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSaveEdit();
                if (e.key === 'Escape') onCancelEdit();
              }}
              style={{
                flex: 1,
                padding: '4px 8px',
                fontSize: 13,
                border: `1px solid ${COLORS.primary}`,
                borderRadius: 4,
              }}
            />
            <button onClick={onSaveEdit} style={iconBtnStyle()}><Check size={14} color={COLORS.success} /></button>
            <button onClick={onCancelEdit} style={iconBtnStyle()}><X size={14} color={COLORS.muted} /></button>
          </>
        ) : (
          <>
            <span className="coa-row-label" style={{
              fontSize: 13,
              fontWeight: isHeading ? 700 : 500,
              color: COLORS.dark,
              flex: 1,
              minWidth: 0,
            }}>
              {row.label}
            </span>
            {row.type === 'account' && row.qboType && (
              <span className="coa-qbo-pill" style={{
                fontSize: 10,
                fontWeight: 600,
                padding: '2px 6px',
                borderRadius: 4,
                background: COLORS.subtle,
                color: COLORS.muted,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}>
                {row.qboType}
              </span>
            )}
            {/* V19 Pass B (2026-06-11) — disclosure_tag dropdown. Only
                rendered for account-type leaves. Default = "—" (null
                tag). Allowed values are enforced both client-side
                (the <option> set below) and server-side (see
                ALLOWED_DISCLOSURE_TAGS in save_client_mapping). The tag
                `income_tax` is intentionally NOT named `tax_expense`
                so payroll/franchise/license accounts cannot be silently
                tagged into the income-tax supplemental line. */}
            {row.type === 'account' && !isEditing && (
              <select
                data-testid={`disclosure-tag-${row.id}`}
                value={row.disclosure_tag || ''}
                onChange={(e) => onSetDisclosureTag(row.id, e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onDragStart={(e) => e.preventDefault()}
                title={
                  row.disclosure_tag
                    ? `Disclosure tag: ${row.disclosure_tag.replace(/_/g, ' ')}`
                    : 'No disclosure tag — set if this account drives the CF D&A add-back, interest disclosure, or income-tax supplemental line.'
                }
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  padding: '2px 4px',
                  border: `1px solid ${row.disclosure_tag ? COLORS.primary : COLORS.border}`,
                  borderRadius: 4,
                  background: row.disclosure_tag ? '#EFF6FF' : '#fff',
                  color: row.disclosure_tag ? COLORS.primary : COLORS.muted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  cursor: 'pointer',
                  minWidth: 84,
                }}
              >
                <option value="">—</option>
                <option value="depreciation_amortization">D &amp; A</option>
                <option value="interest_expense">Interest</option>
                <option value="income_tax">Income Tax</option>
              </select>
            )}
            <span className="coa-row-amount" style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 13,
              fontWeight: isHeading ? 700 : 500,
              color: COLORS.dark,
              minWidth: 110,
              textAlign: 'right',
            }}>
              {formatCurrency(isHeading ? subtotalFor(row.id) : displayAmountFor(row))}
            </span>
            <span className="coa-row-actions" style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <button
                data-testid={`move-up-${row.id}`}
                onClick={(e) => { e.stopPropagation(); moveRow(row.id, 'up'); }}
                title="Move up (Shift+click to move to top)"
                onMouseDown={(e) => e.stopPropagation()}
                onClickCapture={(e) => {
                  if (e.shiftKey) {
                    e.stopPropagation();
                    moveRow(row.id, 'top');
                  }
                }}
                style={iconBtnStyle()}
              >
                <ArrowUp size={13} color={COLORS.muted} />
              </button>
              <button
                data-testid={`move-down-${row.id}`}
                onClick={(e) => { e.stopPropagation(); moveRow(row.id, 'down'); }}
                title="Move down (Shift+click to move to bottom)"
                onMouseDown={(e) => e.stopPropagation()}
                onClickCapture={(e) => {
                  if (e.shiftKey) {
                    e.stopPropagation();
                    moveRow(row.id, 'bottom');
                  }
                }}
                style={iconBtnStyle()}
              >
                <ArrowDown size={13} color={COLORS.muted} />
              </button>
            </span>
            {isHeading && (
              <>
                <button onClick={() => onStartEdit(row)} style={iconBtnStyle()}><Edit2 size={12} color={COLORS.muted} /></button>
                <button onClick={() => onDelete(row.id)} style={iconBtnStyle()}><Trash2 size={12} color={COLORS.error} /></button>
              </>
            )}
          </>
        )}
      </div>

      {/* Children of headings are now flattened by flattenTree() — see parent */}
      {indicator === 'after' && <DropLine />}
    </>
  );
}

// Iteratively flatten the tree under a given parent into [{row, depth}] entries
function flattenTree(rows, parentId, startDepth, matchesSearch) {
  const out = [];
  const walk = (pid, depth) => {
    const kids = rows
      .filter(r => r.parent === pid)
      .sort((a, b) => a.order - b.order);
    for (const k of kids) {
      if (matchesSearch(k) || hasMatchingDescendant(rows, k.id, matchesSearch)) {
        out.push({ row: k, depth });
      }
      if (k.type === 'heading') walk(k.id, depth + 1);
    }
  };
  walk(parentId, startDepth);
  return out;
}

function hasMatchingDescendant(rows, parentId, matchesSearch) {
  for (const r of rows) {
    if (r.parent !== parentId) continue;
    if (matchesSearch(r)) return true;
    if (r.type === 'heading' && hasMatchingDescendant(rows, r.id, matchesSearch)) return true;
  }
  return false;
}

function DropLine({ indent = 0 }) {
  return (
    <div style={{
      height: 2,
      background: COLORS.success,
      margin: '0 18px',
      marginLeft: 18 + indent * 28,
      boxShadow: `0 0 0 2px ${COLORS.success}33`,
      borderRadius: 2,
    }} />
  );
}

function iconBtnStyle() {
  return {
    background: 'transparent',
    border: 'none',
    padding: 4,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
  };
}
